import { z } from 'zod'

import { userApi } from '../users/paths'

/** Where My Competencies lives: the instructor's own course competencies. */
export const myCompetenciesPath = '/site-management/my-competencies'

/** Find Instructors, and the API's search it runs. */
export const instructorsPath = '/site-management/instructors'
export const instructorsApi = '/site-management/v1/instructors'

const instructorSummarySchema = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  displayName: z.string(),
  email: z.string().nullish(),
  organizationId: z.string(),
  organizationName: z.string(),
  isContractInstructor: z.boolean(),
})

/** An instructor as Find Instructors lists them. */
export type InstructorSummary = z.infer<typeof instructorSummarySchema>

export function parseInstructorSummary(body: unknown): InstructorSummary {
  return instructorSummarySchema.parse(body)
}

/** The API's user travel details: GET reads them, PUT replaces them. */
export function userInstructorApi(id: string): string {
  return `${userApi(id)}/instructor`
}

/** The API's user course competencies: GET reads them, PUT replaces them. */
export function userCompetenciesApi(id: string): string {
  return `${userApi(id)}/competencies`
}

/** The API's own course competencies, for My Competencies. */
export const myCompetenciesApi = '/site-management/v1/me/competencies'

/** The API's user contract status: GET reads it, PUT turns it on or off. */
export function userContractStatusApi(id: string): string {
  return `${userApi(id)}/contract-status`
}

/** The caller's own contract reconfirmation prompt: GET says whether it is due, POST answers it. */
export const myContractConsentApi = '/site-management/v1/me/contract-consent'

const contractStatusSchema = z.object({
  isContractInstructor: z.boolean(),
  confirmedAt: z.string().nullish(),
  isConsentDue: z.boolean(),
})

/** Whether a person is a contract instructor, since when, and whether they must reconfirm it. */
export interface ContractStatus {
  readonly isContractInstructor: boolean
  readonly confirmedAt: string | null
  readonly isConsentDue: boolean
}

export const noContractStatus: ContractStatus = { isContractInstructor: false, confirmedAt: null, isConsentDue: false }

/** Whether two contract statuses are the same: on or off, when confirmed, and whether a prompt is due. */
export function sameContract(first: ContractStatus, second: ContractStatus): boolean {
  return (
    first.isContractInstructor === second.isContractInstructor &&
    first.isConsentDue === second.isConsentDue &&
    (first.confirmedAt === null || second.confirmedAt === null
      ? first.confirmedAt === second.confirmedAt
      : Date.parse(first.confirmedAt) === Date.parse(second.confirmedAt))
  )
}

export function parseContractStatus(body: unknown): ContractStatus {
  const parsed = contractStatusSchema.parse(body)
  return { isContractInstructor: parsed.isContractInstructor, confirmedAt: parsed.confirmedAt ?? null, isConsentDue: parsed.isConsentDue }
}

/** The aspects the Instructor tab and My Competencies need. */
export const readInstructorDetailsPermission = 'site-management.user-profiles.instructor-details.read'
export const writeInstructorDetailsPermission = 'site-management.user-profiles.instructor-details.write'
export const readTravelDetailsPermission = 'site-management.user-profiles.travel-details.read'
export const writeTravelDetailsPermission = 'site-management.user-profiles.travel-details.write'

/** The travel details' fields, in the order the tab shows them, as legacy did. */
export const travelFields = ['homeAirport', 'preferredAirline', 'seatPreference', 'frequentFlyerNumber', 'preferredHotel', 'hotelRewardsNumber'] as const

export type TravelField = (typeof travelFields)[number]

/** The most characters each travel field holds. */
export const travelFieldMaxLength = 50

/** An instructor's travel and frequent-flyer details, an empty string for a field not set. */
export type TravelDetails = Readonly<Record<TravelField, string>>

const travelSchema = z.object({
  homeAirport: z.string().nullish(),
  preferredAirline: z.string().nullish(),
  seatPreference: z.string().nullish(),
  frequentFlyerNumber: z.string().nullish(),
  preferredHotel: z.string().nullish(),
  hotelRewardsNumber: z.string().nullish(),
})

export function parseTravelDetails(body: unknown): TravelDetails {
  const parsed = travelSchema.parse(body)
  return Object.fromEntries(travelFields.map((field) => [field, parsed[field] ?? ''])) as TravelDetails
}

export const emptyTravel: TravelDetails = Object.fromEntries(travelFields.map((field) => [field, ''])) as TravelDetails

const competencySchema = z.object({
  courseId: z.string(),
  isCertified: z.boolean(),
  hasPrepared: z.boolean(),
  hasAttended: z.boolean(),
})

/** What an instructor has done to teach one course. */
export type Competency = z.infer<typeof competencySchema>

/** The three things a competency records, in the order legacy showed them. */
export const competencyFlags = ['isCertified', 'hasPrepared', 'hasAttended'] as const

export type CompetencyFlag = (typeof competencyFlags)[number]

const competenciesSchema = z.object({ competencies: z.array(competencySchema) })

export function parseCompetencies(body: unknown): readonly Competency[] {
  return competenciesSchema.parse(body).competencies
}

/** Whether two lists hold the same courses with the same flags, in any order. */
export function sameCompetencies(first: readonly Competency[], second: readonly Competency[]): boolean {
  const byCourse = new Map(first.map((competency) => [competency.courseId, competency]))
  return (
    first.length === second.length &&
    second.every((competency) => {
      const other = byCourse.get(competency.courseId)
      return other !== undefined && competencyFlags.every((flag) => other[flag] === competency[flag])
    })
  )
}

/** Whether two sets of travel details hold the same values, ignoring spaces at the ends. */
export function sameTravel(first: TravelDetails, second: TravelDetails): boolean {
  return travelFields.every((field) => first[field].trim() === second[field].trim())
}
